import React, { useState } from 'react';
import { InputText } from 'primereact/inputtext';

interface GlobalInputFieldProps {
  label?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  icon?: string;
  fullWidth?: boolean;
  autoComplete?: string;
  maxLength?: number;
}

export const GlobalInputField: React.FC<GlobalInputFieldProps> = ({
  label,
  value,
  onChange,
  placeholder = '',
  type = 'text',
  required = false,
  disabled = false,
  error = '',
  icon,
  fullWidth = true,
  autoComplete,
  maxLength,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPasswordField = type === 'password';
  const inputType = isPasswordField && showPassword ? 'text' : type;

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <label style={{
          display: 'block',
          fontSize: '0.75rem',
          fontWeight: 600,
          color: error ? '#dc3545' : '#0f3d3e',
          marginBottom: '10px',
          letterSpacing: '0.2px'
        }}>
          {label} {required && <span style={{ color: '#dc3545' }}>*</span>}
        </label>
      )}
      
      <div style={{ position: 'relative' }}>
        {icon && (
          <i 
            className={icon} 
            style={{
              position: 'absolute',
              left: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#0f3d3e',
              fontSize: '0.85rem',
              pointerEvents: 'none',
              zIndex: 1,
              opacity: 0.5
            }}
          ></i>
        )}
        
        <InputText
          type={inputType}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          maxLength={maxLength}
          style={{
            width: '100%',
            padding: icon ? '11px 14px 11px 42px' : isPasswordField ? '11px 42px 11px 14px' : '11px 14px',
            border: `1px solid ${error ? '#dc3545' : '#e5e7eb'}`,
            borderRadius: '10px',
            fontSize: '0.7rem',
            transition: 'all 0.2s ease',
            background: disabled ? '#f8f9fa' : 'white',
            color: disabled ? '#999' : '#333',
            fontWeight: 500
          }}
          className="global-input-field"
        />

        {isPasswordField && (
          <i 
            className={showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'} 
            onClick={() => !disabled && setShowPassword(!showPassword)}
            style={{
              position: 'absolute',
              right: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#0f3d3e',
              fontSize: '1rem',
              cursor: disabled ? 'not-allowed' : 'pointer',
              zIndex: 1,
              transition: 'color 0.2s ease',
              opacity: disabled ? 0.3 : 0.5
            }}
            onMouseEnter={(e) => !disabled && (e.currentTarget.style.opacity = '1')}
            onMouseLeave={(e) => !disabled && (e.currentTarget.style.opacity = '0.5')}
          ></i>
        )}
      </div>
      
      {error && (
        <span style={{
          display: 'block',
          marginTop: '6px',
          fontSize: '0.75rem',
          color: '#dc3545',
          fontWeight: 500
        }}>
          {error}
        </span>
      )}

      <style>{`
        .global-input-field:enabled:focus {
          outline: 0;
          border-color: #0f3d3e !important;
          box-shadow: 0 0 0 3px rgba(15, 61, 62, 0.1) !important;
        }

        .global-input-field:enabled:hover {
          border-color: #1a5555 !important;
        }

        .global-input-field:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }

        .global-input-field::placeholder {
          color: #999;
          opacity: 0.7;
        }
      `}</style>
    </div>
  );
};
